import { useState, useEffect, forwardRef, useRef } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import TextField from '@mui/material/TextField';
import translator from '@Components/Utilities/Translator';
import loadable from '@loadable/component';

const zxcvbn = loadable(() => import('zxcvbn'));

const PasswordField = forwardRef((props, ref) => {

    const {
        id,
        name,
        value,
        variant,
        onChange,
        label,
        placeholder,
        helperText,
        required,
        fullWidth,
        size
    } = props;

    const defaultInputProps = {
        color: 'primary',
        message: ''
    };

    const [showPassword, setShowPassword] = useState(false);
    const [inputProps, setInputProps] = useState(defaultInputProps);
    const mounted = useRef(false);

    useEffect(() => {
        if (mounted.current) {
            if (value) {
                zxcvbn.load().then(module => {
                    const strength = module.default(value)?.score;
                    if (Number.isInteger(strength)) {
                        switch (strength) {
                            case 0:
                            case 1:
                                setInputProps({
                                    color: 'error',
                                    message: translator.translate('sandhills.tradesites.mvc.weak').toString()
                                });
                                break;
                            case 2:
                                setInputProps({
                                    color: 'warning',
                                    message: translator.translate('sandhillssystem.medium').toString()
                                });
                                break;
                            case 3:
                            case 4:
                                setInputProps({
                                    color: 'success',
                                    message: translator.translate('sandhills.tradesites.mvc.strong').toString()
                                });
                                break;
                            default:
                                setInputProps(defaultInputProps);
                                break;
                        }
                    }
                });
            } else {
                setInputProps(defaultInputProps);
            }
        }
        mounted.current = true;
    }, [value]);

    const toggleShowPassword = () => {
        ref?.blur?.();
        setShowPassword(showPassword => !showPassword);
    };

    return (
        <TextField
            id={id}
            name={name}
            type={showPassword ? 'text' : 'password'}
            variant={variant}
            value={value}
            onChange={onChange}
            label={label}
            placeholder={placeholder}
            required={required}
            fullWidth={fullWidth}
            size={size}
            error={helperText ? true : false}
            helperText={helperText || inputProps.message}
            color={inputProps.color}
            inputRef={ref}
            InputProps={{
                endAdornment: (
                    <InputAdornment position='end'>
                        <IconButton title={showPassword ? translator.translate('sandhills.hidepassword') : translator.translate('sandhills.showpassword')} onClick={toggleShowPassword} edge='end'>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>   
                )
            }}
        />
    );
});

export default PasswordField;